<template>
  <div>

    <!-- Summary Cards -->
    <v-row class="mb-6">
      <v-col cols="12" sm="4">
        <div class="stat-card" style="--card-bar:#F26522;--icon-bg:rgba(242,101,34,0.10);">
          <div class="stat-card-top">
            <div class="stat-card-label">Total Revenue</div>
            <div class="stat-card-icon">
              <v-icon color="#F26522" size="20">mdi-currency-inr</v-icon>
            </div>
          </div>
          <div class="stat-card-value">{{ totalAmount }}</div>
          <div class="stat-card-delta">All time collected</div>
        </div>
      </v-col>
      <v-col cols="12" sm="4">
        <div class="stat-card" style="--card-bar:#3b82f6;--icon-bg:rgba(59,130,246,0.10);">
          <div class="stat-card-top">
            <div class="stat-card-label">Transactions</div>
            <div class="stat-card-icon">
              <v-icon color="#3b82f6" size="20">mdi-receipt-text-outline</v-icon>
            </div>
          </div>
          <div class="stat-card-value">{{ payments.length }}</div>
          <div class="stat-card-delta">Total payment records</div>
        </div>
      </v-col>
      <v-col cols="12" sm="4">
        <div class="stat-card" style="--card-bar:#10b981;--icon-bg:rgba(16,185,129,0.10);">
          <div class="stat-card-top">
            <div class="stat-card-label">Active Societies</div>
            <div class="stat-card-icon">
              <v-icon color="#10b981" size="20">mdi-office-building-outline</v-icon>
            </div>
          </div>
          <div class="stat-card-value">{{ uniqueSocieties }}</div>
          <div class="stat-card-delta">Paid subscriptions</div>
        </div>
      </v-col>
    </v-row>

    <!-- Table -->
    <PremiumTable
      title="Payments"
      subtitle="View all subscription payment transactions."
      :headers="headers"
      :items="payments"
      :loading="loading"
      show-search
      search-placeholder="Search payments..."
    >
      <!-- Plan -->
      <template v-slot:item.plan.title="{ item }">
        <span style="font-weight:600;color:var(--accent);white-space:nowrap;">
          {{ item.plan ? item.plan.title : '—' }}
        </span>
      </template>

      <!-- Society -->
      <template v-slot:item.society.name="{ item }">
        <TruncatedCell :text="item.society ? item.society.name : '—'" max-width="160px" />
      </template>

      <!-- Contact Person -->
      <template v-slot:item.society.contact_person_name="{ item }">
        <div style="display:flex;flex-direction:column;min-width:0;">
          <span style="font-weight:500;white-space:nowrap;">
            {{ item.society ? item.society.contact_person_name : '—' }}
          </span>
          <!-- <span v-if="item.society && item.society.contact_person_email"
            style="font-size:11px;color:var(--text-secondary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:160px;">
            {{ item.society.contact_person_email }}
          </span> -->
        </div>
      </template>

      <!-- Purchase Date -->
      <template v-slot:item.purchase_date="{ item }">
        <span style="white-space:nowrap;">{{ formatDate(item.purchase_date) }}</span>
      </template>

      <!-- Amount -->
      <template v-slot:item.amount="{ item }">
        <span style="font-weight:600;color:var(--text-primary);white-space:nowrap;">
          ₹ {{ item.amount }}
        </span>
      </template>

      <!-- Transaction ID -->
      <template v-slot:item.transaction_id="{ item }">
        <v-tooltip top open-delay="400" content-class="tooltip-top">
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on" class="mono-text">{{ item.transaction_id }}</span>
          </template>
          <span>{{ item.transaction_id }}</span>
        </v-tooltip>
      </template>
    </PremiumTable>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import moment from 'moment';
import PremiumTable from '../common/PremiumTable.vue';
import TruncatedCell from '../common/TruncatedCell.vue';

export default {
  name: 'PaymentsPage',
  components: { PremiumTable, TruncatedCell },

  data() {
    return {
      loading: false,
      payments: [],

      headers: [
        { text: 'Plan',           value: 'plan.title',                  width: '12%' },
        { text: 'Society',        value: 'society.name',                width: '16%' },
        { text: 'Contact Person', value: 'society.contact_person_name', width: '18%' },
        { text: 'Purchase Date',  value: 'purchase_date',               width: '13%' },
        { text: 'Amount',         value: 'amount',                      width: '10%' },
        { text: 'Transaction ID', value: 'transaction_id',              width: '20%' },
      ],
    };
  },

  computed: {
    totalAmount() {
      const sum = this.payments.reduce((acc, p) => acc + Number(p.amount || 0), 0);
      return '₹ ' + sum.toLocaleString('en-IN');
    },
    uniqueSocieties() {
      return new Set(this.payments.map(p => p.society && p.society.id).filter(Boolean)).size;
    },
  },

  methods: {
    ...mapMutations('alert', ['setAlert']),

    formatDate(d) { return moment(d).format('DD MMM, YYYY'); },

    getPayments() {
      this.loading = true;
      this.$axios.get('superadmin/payment').then(res => {
        this.loading = false;
        if (res.data.status) this.payments = res.data.payment;
        else this.setAlert({ showAlert: true, variant: 'red accent-3', timeout: 3000, message: res.data.message });
      }).catch(() => { this.loading = false; });
    },
  },

  mounted() {
    this.getPayments();
  },
};
</script>

<style scoped>
.mono-text {
  font-family: 'JetBrains Mono', 'Courier New', monospace;
  font-size: 11.5px;
  color: var(--text-secondary, #64748b);
  background: var(--bg-primary, #f1f5f9);
  padding: 3px 7px;
  border-radius: 5px;
  border: 1px solid var(--border-color, rgba(0,0,0,0.07));
  white-space: nowrap;
  display: inline-block;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}
</style>